/* global window navigator Blob ClipboardItem PowerPoint document setTimeout */
import * as React from "react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { UndoRedoButtons } from "../providers/UndoRedo";
import { BakeMenu, BakeHoverMenu, Prompt } from "../../assets/icons";
import { useSelector, useDispatch } from "react-redux";
import { SaveRegular, ArrowAutofitContentRegular } from "@fluentui/react-icons";
import { bake } from "../../ppt/bake";
import TrackedButton from "../common/TrackedButton";
import CommonTooltip from "../common/CommonTooltip";
import { notify } from "../providers/GlobalToast";
import { jsonToMarkdown } from "../../helpers/formatting";
import { useStore } from "react-redux";
import { saveGriddToFile } from "../../helpers/fileSystem";
import { runAutofitAll } from "../cells/cellAutofitUtils";

const StackMenu = () => {
  const { t } = useTranslation();
  const store = useStore();
  const dispatch = useDispatch();
  const [mouseOverDocument, setMouseOverDocument] = useState(false);
  const uiScale = useSelector((state) => state.preferences.uiScale);
  const userPrompt = useSelector((state) => state.preferences.userPrompt);
  const localStorageKey = useSelector((state) => state.app.localStorageKey);
  const gridd = useSelector((state) => state.gridd.present);
  const styleSheet = useSelector(
    (state) => state.styleSheet.sheets[state.styleSheet.selectedSheet] ?? state.styleSheet.sheets["default"]
  );

  useEffect(() => {
    function handleMouseMove() {
      setMouseOverDocument(true);
    }

    function handleMouseLeave() {
      setMouseOverDocument(false);
    }

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseout", handleMouseLeave);
  }, []); // TODO: refactor this to a provider instead of two impl, GriddMenu.jsx and here

  const [bakeHovered, setBakeHovered] = React.useState(false);

  const isPowerPoint = typeof PowerPoint !== "undefined";

  return (
    <div
      style={{
        display: "grid",
        gridTemplateRows: "auto auto auto",
        gap: 8,
        paddingBottom: 8,
        visibility: mouseOverDocument ? "visible" : "hidden",
        backgroundColor: "#FFFFFFCC",
      }}
    >
      {!isPowerPoint && !!localStorageKey && (
        <CommonTooltip content={t("tooltip.saveToFile")} positioning="after">
          <TrackedButton
            id="stack_menu_save_to_file_button"
            icon={
              <SaveRegular
                style={{
                  width: 24 * uiScale,
                  height: 24 * uiScale,
                }}
              />
            }
            onClick={async () => {
              await saveGriddToFile(store.getState());
            }}
            appearance="subtle"
            size="small"
            style={{
              minWidth: 0,
              minHeight: 0,
              width: 32 * uiScale,
              height: 32 * uiScale,
            }}
          />
        </CommonTooltip>
      )}
      <CommonTooltip content={t("tooltip.autofit")} positioning="after">
        <TrackedButton
          id="stack_menu_autofit_button"
          icon={
            <ArrowAutofitContentRegular
              style={{
                width: 24 * uiScale,
                height: 24 * uiScale,
              }}
            />
          }
          onClick={() => {
            document.activeElement?.blur();
            setTimeout(() => runAutofitAll(store, dispatch), 0);
          }}
          appearance="subtle"
          size="small"
          style={{
            minWidth: 0,
            minHeight: 0,
            width: 32 * uiScale,
            height: 32 * uiScale,
          }}
        />
      </CommonTooltip>
      <UndoRedoButtons />
      <CommonTooltip content={t("tooltip.bake")} positioning="after">
        <TrackedButton
          id="stack_menu_bake_button"
          icon={
            bakeHovered ? (
              <BakeHoverMenu
                style={{
                  width: 24 * uiScale,
                  height: 24 * uiScale,
                }}
              />
            ) : (
              <BakeMenu
                style={{
                  width: 24 * uiScale,
                  height: 24 * uiScale,
                }}
              />
            )
          }
          onMouseEnter={() => setBakeHovered(true)}
          onMouseLeave={() => setBakeHovered(false)}
          onClick={() => {
            bake(0, gridd, styleSheet);
          }}
          appearance="subtle"
          size="small"
          style={{
            minWidth: 0,
            minHeight: 0,
            width: 32 * uiScale,
            height: 32 * uiScale,
          }}
        />
      </CommonTooltip>

      <CommonTooltip content={t("menu.prompt.tooltip")} positioning="after">
        <TrackedButton
          id="prompt_copy_button"
          icon={
            <Prompt
              style={{
                width: 24 * uiScale,
                height: 24 * uiScale,
              }}
            />
          }
          onClick={() => {
            const state = store.getState();
            const gridd = {
              cells: state.gridd.present.structure.cells.map((rows) =>
                rows.map((c) => ({
                  ...c,
                  json: undefined,
                  id: undefined,
                  text: c.text
                    ?.replace(/<li[^>]*>/gi, "\n * ")
                    ?.replace(/<[^>]*>/g, " ")
                    ?.replace(/\s{2,}/g, "")
                    ?.trim(),
                }))
              ),
            };
            const stringified = t(userPrompt === "" || !userPrompt ? "default.prompt" : userPrompt, {
              json: JSON.stringify(gridd),
              gridd: jsonToMarkdown(state.gridd.present),
              md: gridd.cells.reduce(
                (a, r) =>
                  (a +=
                    r.reduce((p, c) => (p += "|" + c.text?.trim()?.replace("\n", "")?.replace("\r", "")), "") + "|\n"),
                "\n"
              ),
            });
            const griddBlob = new Blob([stringified], { type: "text/plain" });
            navigator.clipboard.write([new ClipboardItem({ "text/plain": griddBlob })]);
            notify("success", "success.copy.prompt");
          }}
          appearance="subtle"
          size="small"
          style={{
            minWidth: 0,
            minHeight: 0,
            width: 32 * uiScale,
            height: 32 * uiScale,
          }}
        />
      </CommonTooltip>
    </div>
  );
};

export default StackMenu;
